// src/lib/app-ws-listener.js
// 2026-09-23 EXTRACTED from src/App.jsx (AppInner «WebSocket Real-Time Sync» effect) —
// behavior-preserving MOVE of the onWsUpdate callback so it can be unit-tested against the
// REAL api-client onmessage (which lsSave's the server snapshot BEFORE calling listeners).
// Differences from the inline original are mechanical only: React setters and the skipSave
// lock arrive through `ctx` instead of the AppInner closure (setDealsData → setters.deals,
// setCrgDeals → setters['crg-deals'], setUsers → setters.users, lockSkipSave(skipSave) →
// lockSkip()). The `setters` map used to be rebuilt per message; the setters are stable
// (useState / useCallback([])), so building it once per effect run is equivalent.

import { lsGet, lsSave } from './local-storage.js';
import { mergeOffersIntoDeals, filterTombstoned } from './tombstones.js';
import { mergeRowsServerWins } from './crg-ws-merge.js';
import { mergeByID } from './merge-by-id.js';
import { isActiveAtomicSnapshot } from './finance-page-merge.js';

/**
 * @param {object} ctx
 * @param {Record<string, Function>} ctx.setters  table → React setter
 * @param {() => void} ctx.lockSkip               lockSkipSave(skipSave) of AppInner
 * @param {(table: string, record: object) => void} ctx.rescue  rescueStaleSyncRecord
 * @param {{ current: Record<string, string> }} ctx.saveBaselines  базы автосейва AppInner
 * @param {{ current: Record<string, string> }} ctx.lastSavedHash  последнее отправленное AppInner
 */
export function createAppWsListener({ setters, lockSkip, rescue, saveBaselines, lastSavedHash }) {
  return (msg) => {
    if (msg.type !== 'update' || !msg.table || !Array.isArray(msg.data)) return;
    // v31.78: observability — log на каждый WS update receipt. Помогает trace
    // путь от server.cjs broadcastUpdate до client setter. Especially CRG где
    // расследуем drop. Memory: parser_diagnostic_log_observability.md +
    // notification_telemetry_required.md — instant ground truth from production.
    // 2026-09-23: сюда же приходят снимки опроса (App.jsx, msg.source === 'poll') — метка в логе.
    const via = msg.source === 'poll' ? 'POLL' : 'WS';
    if (msg.table === 'crg-deals' || msg.table === 'daily-cap') {
      console.log(`📥 ${via} update [${msg.table}]: data.len=${msg.data.length}, deletes=${(msg.deleted||[]).length}, tombstoned=${(msg.tombstoned||[]).length}`);
    }
    // If WS pushes offers update, merge into deals instead
    // v31.09: compound-key merge (см. mergeOffersIntoDeals comment выше)
    if (msg.table === 'offers') {
      const lsDeals = lsGet('deals', null) || [];
      const { merged: mergedRaw, added, replaced } = mergeOffersIntoDeals(lsDeals, msg.data);
      if (added > 0 || replaced > 0) {
        lockSkip();
        const merged = filterTombstoned('deals', mergedRaw); // v11.02 C3: tombstone check
        console.log(`📡 WS offers merge: +${added} new, ~${replaced} replaced (${lsDeals.length} → ${merged.length})`);
        lsSave('deals', merged);
        setters.deals(merged);
      }
      return;
    }
    const setter = setters[msg.table];
    if (setter) {
      // v31.80: для crg-deals и daily-cap — direct server-authoritative replace.
      // Bypasses mergeByID + JSON.stringify check entirely. Diagnosis из v31.78
      // logs показал что server broadcastUpdate fires к 1-2 clients OK,
      // НО UI не обновлялся. v31.79 fixed mergeByID timestamp comparison но
      // user reported still broken. Suspect: либо JSON.stringify equal-check
      // returns false-positive (closure issue / lsData stale read), либо React
      // не re-renders (maybe state ref equality somewhere в memo). Direct
      // setter(msg.data) bypasses all client-side logic — server data всегда
      // wins, fresh array reference forces React re-render. + log fired для
      // verification.
      // ── 2026-06-12 per-row saves fix (incident: owner's 3 CAP Rec edits rolled back) ──
      // ЧТО БЫЛО: blind replace `setter(msg.data)` затирал несохранённые локальные
      // правки (debounce ещё не выстрелил), а auto-save effect ниже «эхом» отстреливал
      // ВЕСЬ stale-массив этой вкладки обратно на сервер после истечения skipSave-окна
      // (skipSave снимался через 2000ms, debounce-retry приходил через 2500ms).
      // ЧТО ТЕПЕРЬ: функциональный row-merge — базис серверный (включая удаления:
      // строки, которых нет на сервере, НЕ воскрешаются), но локальная строка со
      // СТРОГО бОльшим updatedAt (только-что-правка, row-save в полёте) остаётся.
      // Свежая ссылка массива гарантирована (re-render — причина v31.80 replace).
      // skipSave здесь больше НЕ нужен: auto-save effect для crg-deals УДАЛЁН
      // (см. блок debouncedSave-эффектов ниже) — эхо-каскад невозможен структурно.
      // Полный разбор: src/lib/crg-ws-merge.js.
      if (msg.table === 'crg-deals') {
        if (msg.data.length === 0) {
          console.warn(`🛑 ${via} [crg-deals] empty data — skipping (safety)`);
          return;
        }
        setters['crg-deals'](prev => {
          const merged = mergeRowsServerWins(prev, msg.data);
          // WS-слой уже сделал lsSave(msg.data) до listeners (api-client onmessage) —
          // перезаписываем merged-результатом, чтобы LS-кэш не терял local-newer строки.
          lsSave('crg-deals', merged);
          return merged;
        });
        console.log(`✅ ${via} [crg-deals] applied: ${msg.data.length} records (row-merge, local-newer preserved)`);
        return;
      }
      // TODO(next PR): daily-cap — тот же row-merge что crg-deals выше (отдельным PR,
      // у daily-cap свой auto-save отсутствует с v31.18, но blind replace остаётся).
      if (msg.table === 'daily-cap') {
        if (msg.data.length === 0) {
          console.warn(`🛑 ${via} [${msg.table}] empty data — skipping (safety)`);
          return;
        }
        lockSkip();
        lsSave(msg.table, msg.data);
        setter(msg.data);
        console.log(`✅ ${via} [${msg.table}] applied: ${msg.data.length} records (server-authoritative)`);
        return;
      }
      // 2026-06-04: selling-offers — server-authoritative DIRECT replace, same fix as
      // crg-deals/daily-cap above. Server logs confirm the broadcast reaches clients
      // ("📡 BROADCAST [selling-offers] → N clients") but the mergeByID + JSON.stringify
      // equal-check path below did NOT re-render other browsers (the documented closure /
      // stale-lsData false-positive). Direct setter(msg.data) forces a fresh array reference
      // → re-render. Empty allowed (deleting the last offer must sync too).
      if (msg.table === 'selling-offers') {
        lockSkip();
        lsSave('selling-offers', msg.data);
        setter(msg.data);
        return;
      }
      // v12.03 FIX: For users table, server is authoritative — replace, don't merge
      // (mergeByID would re-add deleted users)
      if (msg.table === 'users') {
        const serverUsers = msg.data;
        // FIX: was JSON.stringify comparison — fails if array order differs, update skipped silently
        // Now always apply server users on WS sync (server is authoritative for user list)
        lockSkip();
        lsSave('users', serverUsers);
        setters.users(serverUsers);
        return;
      }
      // 2026-09-23 (N1): aff-caps — серверный массив целиком, как задумано с Daily Cap v2
      // (App.jsx у useState affCaps: «setter заменяет массив целиком»; опрос делает так же).
      // Автосейва в App у таблицы нет, строки правит только модалка своим apiSave — терять
      // нечего. Раньше таблица шла общим путём ниже и с #565 (14.06) экран не обновляла.
      if (msg.table === 'daily-cap-aff-caps') {
        setter(msg.data);
        return;
      }
      // ── 2026-09-23 (N1, ревью Daily Calcs): общий путь — merge В ТЕКУЩИЙ state ──
      // ЧТО БЫЛО: merge с lsGet(msg.table) и `changed = JSON(merged) !== JSON(lsData)`. Но
      // api-client (onmessage) делает lsSave(серверный снимок) ДО листенеров → lsData уже
      // равен серверу → changed=false → setter не звался: экран стоял до перемонтирования
      // страницы, а правка другого поля строки со старого экрана затирала чужую правку
      // целой строкой (row-LWW). Это тот же «stale-lsData false-positive», из-за которого
      // crg-deals (v31.80) и selling-offers (v32.16.11) ушли в свои ветки выше.
      // ЧТО ТЕПЕРЬ: см. mergeSnapshotIntoState. Замок skipSave здесь НЕ берём (раньше брали при
      // changed): замок глобальный, а debouncedSave, попавший в его окно, откладывает себя и
      // сверяет правку с кэшем, куда она ещё не записана, — и молча бросает её. Эхо закрывает
      // сдвиг базы автосейва. Апдейтер сам двигает базу, поэтому повторный вызов с тем же prev
      // (StrictMode) решал бы уже по сдвинутой — отдаём запомненный результат. Другой prev
      // (очередь из нескольких сообщений, перебазирование) считается заново по текущей базе.
      let applied = null;
      setter(prev => {
        if (!applied || applied.prev !== prev) {
          applied = { prev, next: mergeSnapshotIntoState(msg.table, prev, msg.data, { rescue, saveBaselines, lastSavedHash }) };
        }
        return applied.next;
      });
    }
  };
}

/**
 * mergeSnapshotIntoState(table, prev, serverRows, ctx) → следующий state таблицы общего пути.
 * Зовётся ВНУТРИ функционального сеттера: prev — то, что сейчас на экране, включая
 * несохранённые правки (кэш для этого не годится — api-client уже перезаписал его сервером).
 *
 * - Merge = mergeByID(prev, server, 'sync'): серверный базис минус надгробия (api-client уже
 *   внёс msg.deleted/msg.tombstoned в tombstonedIDs) и локальные удаления; локальная строка со
 *   СТРОГО бОльшим updatedAt остаётся; локальная-только моложе 7 дней остаётся (у этих таблиц
 *   автосейв полным массивом с дебаунсом 3 с — строка, созданная в этом окне, ещё не на
 *   сервере), старше — rescue (payments/CP). Поэтому НЕ mergeRowsServerWins, как у crg-deals:
 *   тот стёр бы такую строку, а автосейв затем отправил бы массив без неё.
 * - Строка payments/CP с атомарным запросом в полёте (`_pendingRequestId`, активна) остаётся
 *   своей, даже если серверная новее: ответ запроса ищет строку по этому маркеру и в state, и
 *   в кэше (atomic-reconciliation.js, lsReconcileRecord) — без маркера принятый сервером статус
 *   не сядет на экран, а строка останется со старым. Удалённая сервером — не воскрешается.
 * - deals: на экране серверные сделки со свёрткой офферов из Telegram по aff|country (boot, опрос
 *   и WS offers — mergeOffersIntoDeals). У оффера свой id, поэтому после merge по id свёртку
 *   повторяем с офферами из кэша (его пишут boot/опрос/WS offers) — иначе серверная сделка того
 *   же гео возвращалась рядом с оффером (два прайса), а оффер старше 7 дней выпадал совсем.
 *   Снимок сервера для базы (ниже) сворачивается так же: сервер рассылает deals на каждый POST,
 *   эхо своего сейва приходит раньше HTTP-ответа, и база-«сырой снимок» не совпала бы со
 *   свёрнутым экраном — лишний POST на каждый сейв сделок.
 * - Кэш = результат merge: возвращаем в localStorage несохранённые строки, которые затёр
 *   api-client (иначе F5 их теряет, а performSave бросает правку как «stale closure»).
 * - База автосейва (saveBaselines/lastSavedHash — debouncedSave и гейт payments/CP молчат,
 *   если state совпадает с любой из них). Без этого применение каждого чужого броадкаста
 *   отстреливало бы всю таблицу эхом обратно (класс Aff 140 / crg echo 06-12):
 *     · до броадкаста неотправленного не было (prev = база) → слать нечего и после:
 *       база = результат merge (в нём могут остаться только уже отправленное или то, что
 *       фильтр сейва не пропускает — повторять бессмысленно);
 *     · было → база = снимок сервера (он у сервера точно есть): автосейв отправит ровно
 *       разницу. Порядок строк снимка совпадает с порядком merge — позиционная сверка
 *       гейта атомарных правок (atomic-autosave-suppression.js) не разъезжается.
 * - Ничего не изменилось → тот же prev (без ре-рендера и повторного автосейва).
 */
export function mergeSnapshotIntoState(table, prev, serverRows, { rescue, saveBaselines, lastSavedHash }) {
  const base = Array.isArray(prev) ? prev : [];
  const inFlight = new Map(base.filter((r) => isActiveAtomicSnapshot(r)).map((r) => [r.id, r]));
  const offers = table === 'deals' ? lsGet('offers', null) : null;
  const asShown = Array.isArray(offers) && offers.length > 0
    ? (rows) => filterTombstoned('deals', mergeOffersIntoDeals(rows, offers).merged)
    : (rows) => rows;
  const merged = asShown(
    mergeByID(base, serverRows, table, 'sync', (record) => rescue(table, record))
      .map((r) => inFlight.get(r.id) || r),
  );
  const prevJson = JSON.stringify(base);
  const mergedJson = JSON.stringify(merged);
  const nothingUnsent =
    prevJson === (saveBaselines.current[table] || '[]') ||
    prevJson === (lastSavedHash.current[table] || null);
  const known = nothingUnsent ? mergedJson : JSON.stringify(asShown(serverRows));
  saveBaselines.current[table] = known;
  lastSavedHash.current[table] = known;
  lsSave(table, merged);
  return mergedJson === prevJson ? prev : merged;
}
